import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Building2, Users, FolderOpen, Paperclip } from 'lucide-react';
import { useCompany, useDeleteCompany } from '../hooks/useCompanies.js';
import { useContacts, useDeleteContact } from '../hooks/useContacts.js';
import { useBuckets, useDeleteBucket } from '../hooks/useBuckets.js';
import { toast } from '../lib/toast.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import CompanyModal from '../components/companies/CompanyModal.js';
import ContactModal from '../components/contacts/ContactModal.js';
import BucketModal from '../components/buckets/BucketModal.js';
import type { CompanyType, Contact, Bucket } from '@mayksel/types';
import FileAttachments from '../components/files/FileAttachments.js';
import { useFeatureFlag } from '../lib/posthog.js';

const TYPE_COLORS: Record<CompanyType, string> = {
  prospect: 'var(--color-info)',
  customer: 'var(--color-success)',
  partner: 'var(--color-brand)',
  vendor: 'var(--color-amber)',
};

type Tab = 'overview' | 'contacts' | 'projects' | 'files';

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('companies');
  const { t: tc } = useTranslation('contacts');
  const { t: tb } = useTranslation('buckets');
  const navigate = useNavigate();
  const workspace = useWorkspaceStore((s) => s.workspace);
  const [editOpen, setEditOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('overview');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [bucketModalOpen, setBucketModalOpen] = useState(false);
  const [editingBucket, setEditingBucket] = useState<Bucket | null>(null);

  const filesEnabled = useFeatureFlag('files');
  const { data: company, isLoading, error } = useCompany(id ?? '');
  const { data: contactsData, isLoading: contactsLoading } = useContacts({
    ...(id ? { companyId: id } : {}),
    sortBy: 'last_name', sortDir: 'asc', limit: 100,
  });
  const { data: bucketsData, isLoading: bucketsLoading } = useBuckets({
    ...(id ? { companyId: id } : {}),
    limit: 100,
  });
  const deleteCompany = useDeleteCompany();
  const deleteContact = useDeleteContact();
  const deleteBucket = useDeleteBucket();

  async function handleDelete() {
    if (!company) return;
    if (!confirm(t('detail.delete_confirm', { name: company.name }))) return;
    try {
      await deleteCompany.mutateAsync(company.id);
      toast.success(t('toast.deleted'));
      navigate('/companies');
    } catch (_err) {
      toast.error(t('toast.delete_error'));
    }
  }

  async function handleDeleteContact(contact: Contact) {
    const name = `${contact.firstName} ${contact.lastName}`;
    if (!confirm(tc('detail.delete_confirm', { name }))) return;
    try {
      await deleteContact.mutateAsync(contact.id);
      toast.success(tc('toast.deleted'));
    } catch (_err) {
      toast.error(tc('toast.delete_error'));
    }
  }

  async function handleDeleteBucket(bucket: Bucket) {
    if (!confirm(tb('detail.delete_confirm', { name: bucket.name }))) return;
    try {
      await deleteBucket.mutateAsync(bucket.id);
      toast.success(tb('toast.deleted'));
    } catch (_err) {
      toast.error(tb('toast.delete_error'));
    }
  }

  if (isLoading) {
    return (
      <div className="page-enter">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="skeleton-line" style={{ width: 200, height: 24 }} />
          <div className="skeleton-block" style={{ height: 120, borderRadius: 'var(--radius-lg)' }} />
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="page-enter" style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
        <Building2 size={48} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-4)' }} />
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
          {t('errors.not_found', { ns: 'common' })}
        </p>
      </div>
    );
  }

  return (
    <div className="page-enter">
      {/* Back + header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <button
          onClick={() => navigate(-1)}
          aria-label={t('actions.back', { ns: 'common' })}
          style={{
            background: 'none',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            color: 'var(--color-text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            padding: 'var(--space-1) var(--space-3)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <ArrowLeft size={14} /> {t('actions.back', { ns: 'common' })}
        </button>
        <h1 className="page-title" style={{ flex: 1 }}>{company.name}</h1>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={() => setEditOpen(true)}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text)',
              background: 'var(--color-bg)',
              cursor: 'pointer',
            }}
          >
            {t('detail.edit_button')}
          </button>
          <button
            onClick={handleDelete}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-danger)',
              background: 'var(--color-bg)',
              cursor: 'pointer',
            }}
          >
            {t('detail.delete_button')}
          </button>
        </div>
      </div>

      {/* Summary card */}
      <div
        style={{
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-6)',
          marginBottom: 'var(--space-6)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 'var(--space-5)',
        }}
      >
        {/* Type badges */}
        <div>
          <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('form.type_label')}</p>
          <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
            {company.type.map((ct) => (
              <span
                key={ct}
                style={{
                  padding: '2px var(--space-2)',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-medium)',
                  background: `color-mix(in srgb, ${TYPE_COLORS[ct as CompanyType]} 12%, transparent)`,
                  color: TYPE_COLORS[ct as CompanyType],
                }}
              >
                {t(`types.${ct}`)}
              </span>
            ))}
          </div>
        </div>

        {company.website && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('form.website_label')}</p>
            <a href={company.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-brand)', textDecoration: 'none' }}>
              {company.website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}

        {company.phone && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('form.phone_label')}</p>
            <a href={`tel:${company.phone}`} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{company.phone}</a>
          </div>
        )}

        {company.email && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('form.email_label')}</p>
            <a href={`mailto:${company.email}`} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-brand)', textDecoration: 'none' }}>{company.email}</a>
          </div>
        )}

        {(company.city || company.state) && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>Location</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
              {[company.city, company.state, company.country].filter(Boolean).join(', ')}
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--color-border)', marginBottom: 'var(--space-5)' }}>
        {([
          { key: 'overview', label: t('detail.tab_overview', 'Overview'), icon: null, count: 0 },
          { key: 'contacts', label: tc('list.title'), icon: <Users size={14} />, count: contactsData?.total ?? 0 },
          { key: 'projects', label: workspace?.bucketLabelPlural ?? 'Projects', icon: <FolderOpen size={14} />, count: bucketsData?.total ?? 0 },
          ...(filesEnabled ? [{ key: 'files' as const, label: t('files:tab'), icon: <Paperclip size={14} />, count: 0 }] : []),
        ] as const).map(({ key, label, icon, count }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-4)', background: 'none', border: 'none',
              borderBottom: tab === key ? '2px solid var(--color-brand)' : '2px solid transparent',
              marginBottom: -1, cursor: 'pointer',
              fontSize: 'var(--text-sm)', fontWeight: tab === key ? 'var(--font-medium)' : 'var(--font-regular)',
              color: tab === key ? 'var(--color-brand)' : 'var(--color-text-secondary)',
            }}
          >
            {icon}{label}
            {count > 0 && (
              <span style={{
                background: 'var(--color-brand-pale)', color: 'var(--color-brand)',
                fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)',
                padding: '1px 6px', borderRadius: 'var(--radius-full)',
              }}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === 'overview' && (
        <>
          {company.notes && (
            <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)' }}>
              <p className="label" style={{ marginBottom: 'var(--space-3)' }}>{t('form.notes_label')}</p>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', lineHeight: 'var(--leading-relaxed)', whiteSpace: 'pre-wrap' }}>
                {company.notes}
              </p>
            </div>
          )}
        </>
      )}

      {/* Contacts tab */}
      {tab === 'contacts' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <button
              onClick={() => { setEditingContact(null); setContactModalOpen(true); }}
              style={{
                padding: 'var(--space-2) var(--space-4)', background: 'var(--color-brand)',
                color: 'var(--color-text-inverse)', border: 'none',
                borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)', cursor: 'pointer',
              }}
            >
              + {tc('list.add_button')}
            </button>
          </div>

          {contactsLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton-line" style={{ height: 52, borderRadius: 'var(--radius-md)' }} />
              ))}
            </div>
          )}

          {!contactsLoading && contactsData?.data.length === 0 && (
            <div style={{ textAlign: 'center', padding: 'var(--space-10)', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
              <Users size={32} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-3)', display: 'block', margin: '0 auto var(--space-3)' }} />
              {tc('list.empty_title')}
            </div>
          )}

          {!contactsLoading && contactsData && contactsData.data.length > 0 && (
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              {contactsData.data.map((contact, idx) => (
                <div
                  key={contact.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-4)',
                    padding: 'var(--space-3) var(--space-4)',
                    borderBottom: idx < contactsData.data.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
                    background: 'var(--color-bg)',
                    cursor: 'pointer',
                    transition: 'background var(--duration-snap)',
                  }}
                  onClick={() => navigate(`/contacts/${contact.id}`)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  {/* Avatar initials */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 'var(--radius-full)',
                    background: 'var(--color-brand-pale)', color: 'var(--color-brand)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', flexShrink: 0,
                  }}>
                    {contact.firstName[0]}{contact.lastName[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                      {contact.firstName} {contact.lastName}
                      {contact.companies?.find((c) => c.companyId === id)?.isPrimary && (
                        <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-brand)' }}>
                          {tc('detail.primary_badge')}
                        </span>
                      )}
                    </p>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {[
                        contact.companies?.find((c) => c.companyId === id)?.role,
                        contact.title,
                      ].filter(Boolean).join(' · ') || contact.email}
                    </p>
                  </div>
                  {contact.email && (
                    <a href={`mailto:${contact.email}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: 'var(--text-xs)', color: 'var(--color-brand)', textDecoration: 'none' }}>
                      {contact.email}
                    </a>
                  )}
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }} onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => { setEditingContact(contact); setContactModalOpen(true); }}
                      style={actionBtnStyle} aria-label={`Edit ${contact.firstName}`}>
                      {tc('detail.edit_button')}
                    </button>
                    <button onClick={() => handleDeleteContact(contact)}
                      style={{ ...actionBtnStyle, color: 'var(--color-danger)' }}
                      aria-label={`Delete ${contact.firstName}`}>
                      {t('actions.delete', { ns: 'common' })}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Projects tab */}
      {tab === 'projects' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <button
              onClick={() => { setEditingBucket(null); setBucketModalOpen(true); }}
              style={{
                padding: 'var(--space-2) var(--space-4)', background: 'var(--color-brand)',
                color: 'var(--color-text-inverse)', border: 'none',
                borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)', cursor: 'pointer',
              }}
            >
              + {tb('list.add_button')}
            </button>
          </div>

          {bucketsLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton-line" style={{ height: 52, borderRadius: 'var(--radius-md)' }} />
              ))}
            </div>
          )}

          {!bucketsLoading && bucketsData?.data.length === 0 && (
            <div style={{ textAlign: 'center', padding: 'var(--space-10)', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
              <FolderOpen size={32} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-3)', display: 'block', margin: '0 auto var(--space-3)' }} />
              {tb('list.empty_title')}
            </div>
          )}

          {!bucketsLoading && bucketsData && bucketsData.data.length > 0 && (
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              {bucketsData.data.map((bucket, idx) => (
                <div
                  key={bucket.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-4)',
                    padding: 'var(--space-3) var(--space-4)',
                    borderBottom: idx < bucketsData.data.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
                    background: 'var(--color-bg)', cursor: 'pointer',
                    transition: 'background var(--duration-snap)',
                  }}
                  onClick={() => navigate(`/projects/${bucket.id}`)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  <FolderOpen size={18} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                      {bucket.name}
                    </p>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {tb(`status.${bucket.status}`)}
                      {bucket.value ? ` · ${workspace?.currency ?? 'USD'} ${parseFloat(bucket.value).toLocaleString()}` : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }} onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => { setEditingBucket(bucket); setBucketModalOpen(true); }}
                      style={actionBtnStyle} aria-label={`Edit ${bucket.name}`}>
                      {tb('detail.edit_button')}
                    </button>
                    <button onClick={() => handleDeleteBucket(bucket)}
                      style={{ ...actionBtnStyle, color: 'var(--color-danger)' }}
                      aria-label={`Delete ${bucket.name}`}>
                      {t('actions.delete', { ns: 'common' })}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Files tab */}
      {tab === 'files' && id && (
        <FileAttachments entityType="company" entityId={id} />
      )}

      {editOpen && (
        <CompanyModal company={company} onClose={() => setEditOpen(false)} />
      )}

      {contactModalOpen && (
        <ContactModal
          contact={editingContact}
          defaultCompanyId={id}
          onClose={() => { setContactModalOpen(false); setEditingContact(null); }}
        />
      )}

      {bucketModalOpen && (
        <BucketModal
          bucket={editingBucket}
          defaultCompanyId={id}
          defaultCompanyName={company.name}
          onClose={() => { setBucketModalOpen(false); setEditingBucket(null); }}
        />
      )}
    </div>
  );
}

const actionBtnStyle: React.CSSProperties = {
  padding: '4px 8px', background: 'none', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)',
  color: 'var(--color-text-secondary)', cursor: 'pointer',
};
